import {
  GENERATION_STEP_MS,
  type Channel,
  type Goal,
  type MediaMode,
  type PostFormat,
  type SceneKey,
} from '../constants.js';

export type GeneratedVersion = {
  n: number;
  scene: SceneKey;
  headline: string;
  price: boolean;
  warm: number;
  note: string;
};

export type GeneratorContext = {
  mediaMode: MediaMode;
  goal: Goal;
  /** Short brand handle used in tracked links, e.g. "cedar" */
  brandSlug?: string;
  bookingHost?: string;
};

const ROOM_ORDER: SceneKey[] = ['dusk', 'deck', 'living', 'fire', 'fog', 'bedroom'];
const HEADLINES = [
  'The water is 102°. The whales are on their way.',
  'From the hot tub, at eye level.',
  'Slow mornings. Then whales.',
];

/**
 * Deterministic mock of the content-generation model. A real implementation will call the
 * generation API; the quick-fix vocabulary ("brighter", "different room", ...) is the contract.
 */
export function buildVersion(
  note: string,
  prev: GeneratedVersion | null,
  ctx: GeneratorContext,
): GeneratedVersion {
  const v: GeneratedVersion = prev
    ? { ...prev }
    : {
        n: 0,
        scene: ctx.mediaMode === 'photos' ? 'dusk' : 'fog',
        headline: 'Whale season opens in October.',
        price: false,
        warm: 0,
        note: '',
      };
  const n = note.toLowerCase();
  if (n.includes('room') || n.includes('different') || n.includes('photo')) {
    const idx = ROOM_ORDER.indexOf(v.scene);
    v.scene = ROOM_ORDER[(idx + 1) % ROOM_ORDER.length] ?? 'dusk';
  }
  if (n.includes('price') || n.includes('20%') || n.includes('discount')) v.price = true;
  if (n.includes('bright') || n.includes('warm') || n.includes('light')) {
    v.warm = Math.min(3, v.warm + 1);
  }
  if (n.includes('short')) v.headline = 'Whales. October. Hot tub.';
  if (n.includes('caption') || n.includes('headline') || n.includes('copy')) {
    v.headline = HEADLINES[(prev ? prev.n : 0) % HEADLINES.length] ?? HEADLINES[0]!;
  }
  if (n.includes('lifestyle')) {
    v.scene = 'living';
    v.headline = 'Nothing on the schedule.';
  }
  if (n.includes('less text')) v.headline = 'October.';
  v.n = (prev ? prev.n : 0) + 1;
  v.note = note || 'First draft';
  return v;
}

export function buildCaption(ctx: GeneratorContext, trackedUrl?: string | null): string {
  const base =
    'The water is 102°. The ocean is right there. From October, so are the whales. Midweek stays are 20% off this fall';
  return ctx.goal === 'book' && trackedUrl
    ? `${base} — book direct at ${trackedUrl}.`
    : `${base} — link in bio.`;
}

const CAPTION_ALTS = [
  '102° water, whales at eye level from October, and midweek nights 20% off this fall. That is the whole pitch.',
  'Fog in the morning, whales by October, and the hot tub in between. Midweek is 20% off — link in bio.',
];

/** Returns a new caption when the fix note asks for one, otherwise null (keep the current caption). */
export function captionForFix(note: string, versionN: number): string | null {
  if (!note.toLowerCase().includes('caption')) return null;
  return CAPTION_ALTS[versionN % CAPTION_ALTS.length] ?? null;
}

export function generationStatuses(note: string, mode: MediaMode): string[] {
  if (note) return [`Applying: ${note}…`, 'Composing…'];
  return [
    'Reading your brief…',
    mode === 'photos' ? 'Choosing the photo…' : imageStepText(mode),
    'Writing in your words…',
    'Composing…',
  ];
}

export function generationDurationMs(statusCount: number): number {
  return GENERATION_STEP_MS * statusCount + 300;
}

export const QUICK_FIXES = [
  'Brighter',
  'Different room',
  'New caption',
  'Shorter',
  'More lifestyle',
  'Less text',
] as const;

/* ---------- Content API mapping ---------- */

export type GenerationTarget = 'copy' | 'visuals' | 'whole';
export type ContentFormat = 'image' | 'carousel' | 'story' | 'reel';
export type ContentGoal = 'awareness' | 'bookings';
export type ContentChannel = 'instagram' | 'facebook';
export type ContentStep = 'select_clip_frame' | 'brief' | 'copy' | 'image' | 'video' | 'finish';

export const CONTENT_FORMAT: Record<PostFormat, ContentFormat> = {
  Image: 'image',
  Carousel: 'carousel',
  Story: 'story',
  Reel: 'reel',
};
export const CONTENT_GOAL: Record<Goal, ContentGoal> = { aware: 'awareness', book: 'bookings' };

/** Only Instagram and Facebook shape the caption; other channels send no preference. */
export function contentChannels(channels: readonly Channel[]): ContentChannel[] | undefined {
  const out: ContentChannel[] = [];
  if (channels.includes('IG')) out.push('instagram');
  if (channels.includes('FB')) out.push('facebook');
  return out.length ? out : undefined;
}

const VISUAL_WORDS =
  /\b(bright\w*|dark\w*|warm\w*|cool\w*|light(?:er|ing)?|room|photos?|pictures?|images?|visuals?|scene|lifestyle|colou?r\w*|crop\w*|angle|closer|zoom|railing|fire|flames?|cabin|house|show|videos?|reels?|clips?|film\w*|footage)\b/;
const COPY_WORDS =
  /\b(price|captions?|shorter|longer|less text|more text|copy|headline|text|words?|wording|hashtags?|tone|emojis?|cta|discount|offer|call to action|mention|say)\b/;

/**
 * What a revision should redo: the quick-fix vocabulary and free-form notes map onto the
 * content API's `target`. A note that names both the picture and the words, an empty note, and
 * "Another take" (Regenerate with nothing typed) redo everything. Whole words only, so "context"
 * or "along" never flip the target.
 */
export function generationTarget(note: string): GenerationTarget {
  const n = note.trim().toLowerCase();
  if (!n || n === 'another take') return 'whole';
  const visual = VISUAL_WORDS.test(n);
  const copy = COPY_WORDS.test(n);
  if (visual && copy) return 'whole';
  if (visual) return 'visuals';
  if (copy) return 'copy';
  return 'whole';
}

/** Progress copy for a content-API run, keyed by its reported step. */
/** What the image step is doing, by media mode. */
export function imageStepText(mode: MediaMode): string {
  switch (mode) {
    case 'photos':
      return 'Composing your photo…';
    case 'enhance':
      return 'Enhancing your photo…';
    case 'reimagine':
      return 'Reimagining your photo…';
    case 'imagine':
      return 'Imagining the scene…';
  }
}

export function contentStatusText(
  status: 'queued' | 'running' | 'succeeded' | 'failed',
  step: ContentStep | null,
  mode: MediaMode,
): string {
  if (status === 'queued') return 'Waiting in line…';
  switch (step) {
    case 'brief':
      return 'Reading your brief…';
    case 'copy':
      return 'Writing in your words…';
    case 'select_clip_frame':
      return 'Finding the frame to edit…';
    case 'image':
      return imageStepText(mode);
    case 'video':
      return 'Filming the reel…';
    case 'finish':
      return 'Finishing up…';
    default:
      return 'Composing…';
  }
}

export function contentProgress(
  state: 'uploading' | 'creating' | 'generating',
  step: ContentStep | null,
): number {
  if (state === 'uploading') return 0.1;
  if (state === 'creating') return 0.25;
  switch (step) {
    case 'brief':
      return 0.4;
    case 'copy':
      return 0.55;
    case 'select_clip_frame':
      return 0.65;
    case 'image':
      return 0.75;
    case 'video':
      return 0.85;
    case 'finish':
      return 0.95;
    default:
      return 0.3;
  }
}

/**
 * The caption we publish: the model's caption, our tracked link, then the hashtags. A bookings
 * caption already ends with a book-direct invitation (the API leaves the URL to us), so the link
 * follows that line on its own; otherwise it gets a short lead-in.
 */
export function composeCaption(
  output: { caption: string; hashtags: string[] },
  trackedUrl: string | null,
): string {
  const caption = output.caption.trim();
  const parts = [caption];
  if (trackedUrl) {
    const lastParagraph = caption.split(/\n\s*\n/).pop() ?? '';
    if (/book direct/i.test(lastParagraph)) parts[0] = `${caption}\n${trackedUrl}`;
    else parts.push(`Book direct: ${trackedUrl}`);
  }
  if (output.hashtags.length)
    parts.push(output.hashtags.map((h) => `#${h.replace(/^#/, '')}`).join(' '));
  return parts.filter(Boolean).join('\n\n');
}
